function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useSelector } from 'react-redux';
import moment from 'moment';
import get from 'lodash/get';
import toPairs from 'lodash/toPairs';
import filter from 'lodash/filter';
import isEqual from 'lodash/isEqual';
import { useAction, useProgressSuccess } from 'hooks';
import FormGroup from 'components/Form/FormGroup';
import Button from 'components/Form/Button';
import Select from 'components/Form/Select';
import Form from 'components/Form';
import App from 'modules/App';
import Account from 'modules/Account';
import messages from '../../../messages';
import * as actions from '../../../actions';
import * as selectors from '../../../selectors';
import * as constants from '../../../constants';
import RangePicker from './RangePicker';
import validatorRules from './validatorRules.json';
import styles from './NotificationFilters.pcss';
const formName = constants.NOTIFICATIONS_FILTERS_FORM;
const Actions = ({ onSubmit, clearForm })=>{
    const notificationsFilter = useSelector(selectors.notificationsFilter);
    const isFetchNotificationsInProgress = useSelector(selectors.isFetchNotificationsInProgress);
    const setNotificationFilterParams = useAction(actions.setNotificationFilterParams, constants.DEFAULT_NOTIFICATION_FILTER);
    const formValues = App.hooks.useFormValues(formName);
    const originalValues = get(formValues, 'originalValues');
    const isDisabled = !originalValues;
    const isInProgress = get(formValues, 'processing');
    const isClean = isEqual(notificationsFilter, constants.DEFAULT_NOTIFICATION_FILTER);
    const clear = ()=>{
        clearForm();
        setNotificationFilterParams();
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Button, {
                type: "submit",
                styleModifier: "primary",
                labelMessage: messages.buttons.apply,
                className: "btn--sm btn--transparent",
                isDisabled: isDisabled,
                isInProgress: isInProgress || isFetchNotificationsInProgress,
                onClick: onSubmit
            }),
            /*#__PURE__*/ _jsx(Button, {
                type: "button",
                styleModifier: "secondary",
                labelMessage: messages.buttons.clearAll,
                className: "btn--sm btn--transparent",
                isInProgress: isInProgress,
                isDisabled: isDisabled && isClean,
                onClick: clear
            })
        ]
    });
};
const NotificationsFilters = ({ toggleView })=>{
    useStyles(styles);
    const intl = useIntl();
    const localizationResources = useSelector(App.selectors.localizationResources);
    const notificationsSettings = useSelector(Account.selectors.allNotificationsSettings);
    const notificationsFilter = useSelector(selectors.notificationsFilter);
    const isFetchNotificationsInProgress = useSelector(selectors.isFetchNotificationsInProgress);
    const hasFetchNotificationsErrors = useSelector(selectors.hasFetchNotificationsErrors);
    const setFormValue = useAction(App.actions.setFormValue, formName);
    const setFormValues = useAction(App.actions.setFormValues, formName);
    const clearForm = useAction(App.actions.clearForm, formName);
    const setNotificationFilterParams = useAction(actions.setNotificationFilterParams);
    const [focusedInput, setFocusedInput] = useState();
    const formValues = App.hooks.useFormValues(formName);
    const notificationStatusesSelectedOptions = App.hooks.useFieldValue(formName, 'notificationStatuses');
    const notificationTriggersSelectedOptions = App.hooks.useFieldValue(formName, 'notificationTriggers');
    const fromTimestamp = App.hooks.useFieldValue(formName, 'fromTimestamp');
    const toTimestamp = App.hooks.useFieldValue(formName, 'toTimestamp');
    const submitForm = (validatedValues)=>{
        setNotificationFilterParams(validatedValues);
    };
    const processForm = App.hooks.useFormProcessing(formName, submitForm);
    const onSubmit = ()=>{
        processForm(formValues, validatorRules);
    };
    useEffect(()=>{
        setFormValues(notificationsFilter);
        return ()=>{
            clearForm();
        };
    }, []);
    useProgressSuccess(isFetchNotificationsInProgress, hasFetchNotificationsErrors, toggleView);
    const onDatesChange = (startDate, endDate)=>{
        setFormValue({
            id: 'fromTimestamp',
            value: startDate ? startDate.unix() : null
        });
        setFormValue({
            id: 'toTimestamp',
            value: endDate ? endDate.unix() : null
        });
    };
    const notificationTriggersOptions = (notificationsSettings || []).map(({ notificationTrigger, descriptionKey })=>({
            value: notificationTrigger,
            label: get(localizationResources, `${descriptionKey}.value`, descriptionKey)
        }));
    const onSelectType = ({ id, value })=>{
        if (notificationTriggersSelectedOptions && notificationTriggersSelectedOptions.includes(value)) {
            const valuesToSet = filter(notificationTriggersSelectedOptions, (option)=>option !== value);
            setFormValue({
                id,
                value: valuesToSet
            });
        } else {
            setFormValue({
                id,
                value: [
                    ...notificationTriggersSelectedOptions,
                    value
                ]
            });
        }
    };
    const notificationStatusesOptions = toPairs(constants.NOTIFICATIONS_STATUSES).map(([key, value])=>({
            value: key,
            label: intl.formatMessage(_object_spread({}, messages.status[value.toLowerCase()]))
        }));
    const onSelectStatus = ({ id, value })=>{
        if (notificationStatusesSelectedOptions && notificationStatusesSelectedOptions.includes(value)) {
            const valuesToSet = filter(notificationStatusesSelectedOptions, (option)=>option !== value);
            setFormValue({
                id,
                value: valuesToSet
            });
        } else {
            setFormValue({
                id,
                value: [
                    ...notificationStatusesSelectedOptions,
                    value
                ]
            });
        }
    };
    const closeCalendar = ()=>{
        if (focusedInput) {
            setFocusedInput(null);
        }
    };
    return /*#__PURE__*/ _jsxs(Form, {
        className: styles.notificationsFilters,
        onSubmit: onSubmit,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "form-group",
                children: [
                    /*#__PURE__*/ _jsx("label", {
                        className: "form-label",
                        htmlFor: "dateRangePicker",
                        children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.labels.dateRangePicker))
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.notificationsFilters__dateRangePicker,
                        children: /*#__PURE__*/ _jsx(RangePicker, {
                            startDate: fromTimestamp ? moment.unix(fromTimestamp) : null,
                            endDate: toTimestamp ? moment.unix(toTimestamp) : null,
                            maxDate: moment(),
                            onChange: onDatesChange,
                            onClose: closeCalendar
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(FormGroup, {
                id: "notificationTriggers",
                labelMessage: messages.labels.type,
                formValues: formValues,
                children: /*#__PURE__*/ _jsx(Select, {
                    optionsFrom: notificationTriggersOptions,
                    valueKey: "value",
                    labelKey: "label",
                    onChange: onSelectType,
                    multi: true
                })
            }),
            /*#__PURE__*/ _jsx(FormGroup, {
                id: "notificationStatuses",
                labelMessage: messages.labels.status,
                formValues: formValues,
                children: /*#__PURE__*/ _jsx(Select, {
                    optionsFrom: notificationStatusesOptions,
                    valueKey: "value",
                    labelKey: "label",
                    onChange: onSelectStatus,
                    multi: true
                })
            }),
            /*#__PURE__*/ _jsx(Actions, {
                onSubmit: onSubmit,
                clearForm: clearForm
            })
        ]
    });
};
export default NotificationsFilters;
